<div class="env-detail-page" [class.embedded]="embedded" [class.in-dialog]="inDialog">
  <div class="section-header">
    <h2>
      <button mat-icon-button type="button" class="section-header-back-btn"
        (click)="goBack()" [matTooltip]="inDialog ? 'Close' : (embedded ? 'Back to List' : 'Back to Admin Panel')"
        [attr.aria-label]="inDialog ? 'Close' : (embedded ? 'Back to List' : 'Back to Admin Panel')">
        <mat-icon>{{ inDialog ? 'close' : 'arrow_back' }}</mat-icon>
      </button>
      <mat-icon>cloud</mat-icon>
      Environment Details
    </h2>
    <div class="section-header-actions">
      <button mat-stroked-button color="primary" type="button" (click)="loadEnvironment()" [disabled]="loading">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </div>

  <div class="env-detail-body mat-elevation-z1">
    <div class="loading-state" *ngIf="loading">
      <mat-spinner diameter="36"></mat-spinner>
      <p>Loading environment...</p>
    </div>

    <div class="error-state" *ngIf="!loading && errorMessage">
      <mat-icon>error_outline</mat-icon>
      <p>{{ errorMessage }}</p>
    </div>

    <ng-container *ngIf="!loading && !errorMessage && environment">
      <div class="env-title-row">
        <div class="env-title-copy">
          <h2><code>{{ environment.name }}</code></h2>
          <div class="env-title-meta">
            <span class="env-status-badge" [ngClass]="'env-status-' + (environment.state || '').toLowerCase()">
              {{ environment.state }}
            </span>
            <span class="env-owner-chip">
              <mat-icon>person</mat-icon>
              {{ environment.owner || owner }}
            </span>
          </div>
        </div>
        <div class="env-detail-actions">
          <button mat-stroked-button color="primary" type="button"
            (click)="resumeEnvironment()"
            [disabled]="!canResume() || anyActionBusy"
            [class.env-btn-disabled]="!canResume()">
            <mat-spinner *ngIf="isActionBusy('restart')" diameter="14" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActionBusy('restart')">play_arrow</mat-icon>
            Resume
          </button>
          <button mat-stroked-button color="accent" type="button"
            (click)="stopEnvironment()"
            [disabled]="!canStop() || anyActionBusy"
            [class.env-btn-disabled]="!canStop()">
            <mat-spinner *ngIf="isActionBusy('stop')" diameter="14" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActionBusy('stop')">pause_circle</mat-icon>
            Stop
          </button>
          <button mat-flat-button color="warn" type="button"
            (click)="confirmDelete()"
            [disabled]="!canDelete() || anyActionBusy"
            [class.env-btn-disabled]="!canDelete()">
            <mat-spinner *ngIf="isActionBusy('delete')" diameter="14" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActionBusy('delete')">delete_forever</mat-icon>
            Delete
          </button>
        </div>
      </div>

      <section class="detail-section">
        <h3 class="group-title">Overview</h3>
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Name</span><span class="detail-value">{{ environment.name }}</span></div>
          <div class="detail-item"><span class="detail-label">Status</span><span class="detail-value">{{ environment.state }}</span></div>
          <div class="detail-item"><span class="detail-label">Owner (email)</span><span class="detail-value">{{ environment.owner || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">Owner ID</span><span class="detail-value">{{ environment.ownerId || owner }}</span></div>
          <div class="detail-item"><span class="detail-label">Type</span><span class="detail-value">{{ environment.type || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">Size</span><span class="detail-value">{{ environment.size || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">OAF State</span><span class="detail-value">{{ environment.oafState || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">Age</span><span class="detail-value">{{ getAge() }}</span></div>
        </div>
      </section>

      <section class="detail-section">
        <h3 class="group-title">Location & Network</h3>
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Region</span><span class="detail-value">{{ environment.region || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">Zone</span><span class="detail-value">{{ environment.zone || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">Zone Name</span><span class="detail-value">{{ environment.zoneName || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">IP Address</span><span class="detail-value">{{ environment.ip || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">DNS Name</span><span class="detail-value">{{ environment.dnsName || '-' }}</span></div>
          <div class="detail-item"><span class="detail-label">Web DNS Name</span><span class="detail-value">{{ environment.webDnsName || '-' }}</span></div>
        </div>
      </section>

      <section class="detail-section">
        <h3 class="group-title">Timeline</h3>
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Created</span><span class="detail-value">{{ formatDate(environment.createDate) }}</span></div>
          <div class="detail-item"><span class="detail-label">Starting</span><span class="detail-value">{{ formatDate(environment.startingTime) }}</span></div>
          <div class="detail-item"><span class="detail-label">Started</span><span class="detail-value">{{ formatDate(environment.startedTime) }}</span></div>
          <div class="detail-item"><span class="detail-label">Stopping</span><span class="detail-value">{{ formatDate(environment.stoppingTime) }}</span></div>
          <div class="detail-item"><span class="detail-label">Stopped</span><span class="detail-value">{{ formatDate(environment.stoppedTime) }}</span></div>
        </div>
      </section>

      <section class="detail-section">
        <div class="group-title-row">
          <h3 class="group-title">Notebooks</h3>
          <a *ngIf="notebookInfo?.jupyterUrl" mat-stroked-button color="primary"
            [href]="notebookInfo!.jupyterUrl" target="_blank" rel="noopener noreferrer"
            matTooltip="Open this environment's JupyterLab as an admin">
            <mat-icon>open_in_new</mat-icon>
            View as Admin
          </a>
          <span *ngIf="!notebooksLoading && !notebookInfo?.jupyterUrl"
            matTooltip="Admin access link is only available while the environment is running">
            <button mat-stroked-button type="button" disabled>
              <mat-icon>open_in_new</mat-icon>
              View as Admin
            </button>
          </span>
        </div>

        <div class="notebooks-loading" *ngIf="notebooksLoading">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Loading notebook activity…</span>
        </div>

        <p class="notebooks-empty" *ngIf="!notebooksLoading && notebooksError">{{ notebooksError }}</p>

        <p class="notebooks-empty" *ngIf="!notebooksLoading && !notebooksError && notebookInfo?.message">
          {{ notebookInfo!.message }}
        </p>

        <p class="notebooks-empty" *ngIf="!notebooksLoading && !notebooksError && !notebookInfo">
          No notebook activity found for this environment.
        </p>

        <table class="notebooks-table" *ngIf="!notebooksLoading && notebookInfo && notebookInfo.notebooks.length > 0">
          <thead>
            <tr>
              <th class="nb-col-name">Notebook</th>
              <th class="nb-col-count">Opens</th>
              <th class="nb-col-time">Last Opened</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let nb of notebookInfo.notebooks">
              <td class="nb-col-name">{{ nb.name }}</td>
              <td class="nb-col-count">{{ nb.openCount }}</td>
              <td class="nb-col-time">{{ nb.lastOpened }}</td>
            </tr>
          </tbody>
        </table>
      </section>


      <ng-container *ngIf="environment.startupScriptResult">
        <section class="detail-section">
          <h3 class="group-title">Startup Script Result</h3>
          <div class="script-block" *ngIf="environment.startupScriptResult.stdout">
            <span class="detail-label">stdout</span>
            <pre>{{ environment.startupScriptResult.stdout }}</pre>
          </div>
          <div class="script-block" *ngIf="environment.startupScriptResult.stderr">
            <span class="detail-label">stderr</span>
            <pre>{{ environment.startupScriptResult.stderr }}</pre>
          </div>
        </section>
      </ng-container>

      <section class="detail-section">
        <h3 class="group-title">Event History</h3>
        <div class="events-loading" *ngIf="eventsLoading">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Loading event history…</span>
        </div>

        <p class="events-empty" *ngIf="!eventsLoading && eventsError">{{ eventsError }}</p>

        <p class="events-empty" *ngIf="!eventsLoading && !eventsError && events.length === 0">
          No events found for this environment.
        </p>

        <div class="events-timeline" *ngIf="!eventsLoading && !eventsError && events.length > 0">
          <div class="event-item" *ngFor="let event of events" [ngClass]="'event-severity-' + (event.severity || 'INFO').toLowerCase()">
            <div class="event-header">
              <span class="event-type">{{ event.type }}</span>
              <span class="event-time">{{ formatDate(event.timestamp) }}</span>
            </div>
            <div class="event-details">
              <span class="event-initiator">
                <strong>Initiator:</strong> {{ formatInitiator(event) }}
              </span>
              <span class="event-severity" [ngClass]="'severity-' + (event.severity || 'INFO').toLowerCase()">
                {{ event.severity || 'INFO' }}
              </span>
            </div>
            <div class="event-metadata" *ngIf="event.details && Object.keys(event.details).length > 0">
              <div *ngFor="let key of Object.keys(event.details)" class="event-meta-item">
                <span class="meta-key">{{ key }}:</span>
                <span class="meta-value">{{ formatMetadataValue(event.details[key]) }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ng-container>
  </div>

</div>
